import { useId, useState } from 'react'
import { Icon } from '../components/Icon'
import { FieldRenderer } from '../components/FieldRenderer'
import { Modal } from '../components/Modal'
import { useTerms } from '../i18n/TermsContext'
import type { Customer, FieldDef, Ticket } from '../domain/types'

// Cancelamento de UM bilhete da jornada (não a jornada inteira). Coleta motivo + campos OnCancel.
// Espelha o CancelModal de jornada, mas escopado ao bilhete — entrada pelo TicketDetailModal.
export function TicketCancelModal({ ticket, cliente, fields, submitting = false, onClose, onConfirm }: {
  ticket: Ticket
  cliente: Customer
  fields: FieldDef[]
  submitting?: boolean
  onClose: () => void
  onConfirm: (motivo: string, values: Record<string, string>) => void
}) {
  const titleId = useId()
  const [motivo, setMotivo] = useState('')
  const [preset, setPreset] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const terms = useTerms()
  const REASONS = [`${terms.subject} desistiu`, `${terms.subject} não compareceu`, 'Empresa cancelou', 'Duplicidade', 'Erro de cadastro']
  const fieldsOk = fields.every((f) => !f.required || (values[f.key] || '').trim() !== '')

  const pick = (r: string) => { if (!submitting) { setPreset(r); setMotivo(r) } }
  const confirm = () => { if (!submitting && motivo.trim() && fieldsOk) {onConfirm(motivo.trim(), values)} }

  return (
    <Modal
      open
      onClose={submitting ? () => {} : onClose}
      titleId={titleId}
      title="Cancelar este bilhete?"
      closeOnOverlay={!submitting}
    >
      <div data-screen-label="Cancelar serviço">
        <div className="fz-modal__icon"><Icon name="alert" size={24} /></div>
        <div className="fz-modal__title" id={titleId}>Cancelar este bilhete?</div>
        <div className="fz-modal__text">
          Você vai cancelar apenas o bilhete <b>{ticket.codigoTicket || ticket.nomeServico}</b> ({ticket.nomeServico}) de <b>{cliente.nome}</b>.
          {' '}Os demais serviços da jornada permanecem.
        </div>
        <div className="fz-field">
          <label className="fz-field__label">Motivo do cancelamento (obrigatório)</label>
          <textarea value={motivo} placeholder="Descreva o motivo…" disabled={submitting} onChange={(e) => { setMotivo(e.target.value); setPreset(null) }} />
          <div className="fz-reasons">
            {REASONS.map((r) => (
              <button key={r} className={'fz-reason ' + (preset === r ? 'is-active' : '')} disabled={submitting} onClick={() => pick(r)}>{r}</button>
            ))}
          </div>
        </div>
        {fields.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, textAlign: 'left' }}>
            {fields.map((f, i) => (
              <FieldRenderer
                key={f.key}
                def={f}
                autoFocus={i === 0}
                value={values[f.key] || ''}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
              />
            ))}
          </div>
        )}
        <div className="fz-modal__actions">
          <button className="fz-btn fz-btn--neutral fz-btn--block" onClick={onClose} disabled={submitting}>Voltar</button>
          <button className="fz-btn fz-btn--primary fz-btn--block" style={{ background: motivo.trim() && fieldsOk ? 'var(--color-danger)' : undefined }} disabled={!motivo.trim() || !fieldsOk || submitting} aria-busy={submitting} onClick={confirm}>
            {submitting ? <><span className="fz-spin" /> Cancelando…</> : 'Confirmar cancelamento'}
          </button>
        </div>
      </div>
    </Modal>
  )
}